import { useState, useEffect, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import { captureWithMessage } from '@/lib/captureWithMessage';
import { Dumbbell, Flame, Plus, History, X, ZoomIn, RotateCcw, Check, Save, Pencil, Trash2, Timer, Video, ChevronDown, ChevronUp } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { ScrollHintRow } from './ScrollHintRow';
import { RoutineDay } from '@/types/fitness';
import { TempoDisplay } from './TempoDisplay';
import { useRestTimer } from '@/contexts/RestTimerContext';
import { supabase } from '@/integrations/supabase/client';
import { getPrimaryCoachId } from '@/lib/primaryCoach';
import { useAuth } from '@/hooks/useAuth';
import { cachedQuery, offlineMutation, setCachedData, getCachedData, removeCachedData } from '@/lib/offlineStorage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getExerciseImage } from '@/data/exerciseImages';
import { youtubeThumbnail, youtubeThumbnailFallbacks } from '@/lib/videoUrl';
import { ResilientImage } from '@/components/ResilientImage';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

interface RoutineSectionProps {
  rutina: RoutineDay[];
  activeUser: string;
  viewingUserId?: string | null;
  onCardioLogged?: () => void;
}

interface WeightLog {
  id: number;
  exercise_name: string;
  weight_value: number;
  weight_unit: string;
  weight_mode: string;
  log_date: string;
  notes: string | null;
}

interface SessionState {
  completed: boolean;
  notes: string;
}

type WeightUnit = 'Lbs' | 'Kg' | 'Placas' | 'Discos';
type WeightMode = 'total' | 'por_lado';

interface CardioData { __cardio: true; aparato?: string; duracion?: string; calorias?: string; velocidad?: string; inclinacion?: string; }
interface PendingCardioLog { ejercicioNombre: string; defaultCals: number; defaultDuration: string; aparato: string; }
const parseCardioSpec = (spec: string | null | undefined): CardioData | null => {
  if (!spec) return null;
  try { const d = JSON.parse(spec); if (d.__cardio) return d as CardioData; } catch {}
  return null;
};

// Dynamic subtitle — current month and year in Spanish
function getCurrentMonthYear(): string {
  const now = new Date();
  return now.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
    .replace(/^\w/, c => c.toUpperCase());
}

export function RoutineSection({ rutina, activeUser, viewingUserId, onCardioLogged }: RoutineSectionProps) {
  const isCoachView = Boolean(viewingUserId);
  // Default al día de la semana actual: si hoy es martes, el cliente
  // arranca en el martes de su rutina sin tener que cambiar manualmente.
  // Si la rutina no tiene el día actual (ej: hoy es domingo y solo
  // entrena L-V), cae al primer día de la rutina.
  const [selectedDay, setSelectedDay] = useState(() => {
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const todayName = dayNames[new Date().getDay()];
    const idx = rutina.findIndex(d => d.dia === todayName);
    return idx >= 0 ? idx : 0;
  });
  const [weights, setWeights] = useState<Record<string, WeightLog[]>>({});
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [newWeight, setNewWeight] = useState('');
  const [newUnit, setNewUnit] = useState<WeightUnit>('Lbs');
  const [newMode, setNewMode] = useState<WeightMode>('total');
  const [showHistoryFor, setShowHistoryFor] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [videoPlayer, setVideoPlayer] = useState<{ url: string; type: string; name: string } | null>(null);
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);
  const [sessionState, setSessionState] = useState<Record<string, SessionState>>({});
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [editingWeightId, setEditingWeightId] = useState<number | null>(null);
  const [editWeightValue, setEditWeightValue] = useState('');
  const [editWeightUnit, setEditWeightUnit] = useState<WeightUnit>('Lbs');
  const [editWeightMode, setEditWeightMode] = useState<WeightMode>('total');
  const [pendingCardio, setPendingCardio] = useState<PendingCardioLog | null>(null);
  const [cardioRealCals, setCardioRealCals] = useState('');
  const [cardioRealDuration, setCardioRealDuration] = useState('');
  // Map de "Lunes_2026-05-04" → % registrado en BD (-1 = nunca registrado)
  const [confirmedDays, setConfirmedDays] = useState<Record<string, number>>({});
  // Ref para capturar el header del día como screenshot
  const dayHeaderRef = useRef<HTMLDivElement>(null);

  // Timer global — el cliente puede minimizar y navegar la rutina
  // sin que el cronómetro se detenga.
  const restTimer = useRestTimer();
  const openTimer = (restOptions?: string[], exerciseName?: string) => {
    restTimer.openModal({
      restOptions: restOptions && restOptions.length > 0 ? restOptions : undefined,
      exerciseName,
    });
  };
  const { user } = useAuth();
  const day = rutina[selectedDay];
  // Fecha LOCAL (no UTC) para que coincida con la zona horaria del cliente
  const today = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  const subtitle = getCurrentMonthYear();

  useEffect(() => {
    if (!user) return;
    fetchWeights();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchSessionState();
  }, [user, selectedDay]);

  // Cargar confirmaciones de hoy al cambiar de día — vía cachedQuery
  // para que offline siga mostrando los entrenamientos confirmados
  // (con su % de completado) en vez de quedarse esperando la red.
  useEffect(() => {
    if (!user || isCoachView) return;
    (async () => {
      const { data } = await cachedQuery<any[]>(
        `completion_logs_${user.id}_${activeUser}_${today}`,
        () => supabase
          .from('routine_completion_logs')
          .select('day_name, completion_percentage')
          .eq('user_id', user.id)
          .eq('profile_name', activeUser)
          .eq('completion_date', today)
      );
      if (data && data.length > 0) {
        const confirmed: Record<string, number> = {};
        data.forEach((r: any) => {
          confirmed[`${r.day_name}_${today}`] = Number(r.completion_percentage) || 0;
        });
        setConfirmedDays(confirmed);
      }
    })();
  }, [user, activeUser, today]);

  const fetchWeights = async () => {
    if (!user) return;
    const targetUserId = viewingUserId || user.id;
    // Filter by user_id only — no profile_name
    const { data } = await cachedQuery<any[]>(`weights_${targetUserId}`, () =>
      supabase
        .from('exercise_weights')
        .select('*')
        .eq('user_id', targetUserId)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false })
    );
    if (data) {
      const grouped: Record<string, WeightLog[]> = {};
      data.forEach((w: WeightLog) => {
        if (!grouped[w.exercise_name]) grouped[w.exercise_name] = [];
        grouped[w.exercise_name].push(w);
      });
      setWeights(grouped);
    }
  };

  const fetchSessionState = async () => {
    if (!user) return;
    const targetUserId = viewingUserId || user.id;

    // Two parallel reads:
    //   1. TODAY's session — for completion state and any note edited today
    //   2. ALL historical notes for this day's exercises — so notes persist
    //      across days. Notes are observations about the exercise (e.g.
    //      "subir 5kg") and must not disappear when session_date rolls
    //      over to a new day. Today's note (if any) takes priority over
    //      historical, so the user always sees their latest edit.
    const [todayRes, historyRes] = await Promise.all([
      cachedQuery<any[]>(`session_${targetUserId}_${day.dia}_${today}`, () =>
        supabase
          .from('workout_session')
          .select('*')
          .eq('user_id', targetUserId)
          .eq('day_name', day.dia)
          .eq('session_date', today)
      ),
      // Notas históricas también cacheadas — son las observaciones que
      // el cliente fue dejando ("subir 5kg") y deben sobrevivir offline
      // junto con los checkboxes del día.
      cachedQuery<any[]>(`session_notes_${targetUserId}_${day.dia}`, () =>
        supabase
          .from('workout_session')
          .select('exercise_name, notes, session_date')
          .eq('user_id', targetUserId)
          .eq('day_name', day.dia)
          .not('notes', 'is', null)
          .neq('notes', '')
          .order('session_date', { ascending: false })
          .limit(500)
      ),
    ]);

    // Build a map of exercise -> most recent note across all dates.
    const latestNoteByExercise: Record<string, string> = {};
    for (const row of (historyRes.data || []) as any[]) {
      if (row.exercise_name && !latestNoteByExercise[row.exercise_name] && row.notes) {
        latestNoteByExercise[row.exercise_name] = row.notes;
      }
    }

    const state: Record<string, SessionState> = {};
    const drafts: Record<string, string> = {};
    const todayMap: Record<string, any> = {};
    for (const r of (todayRes.data || []) as any[]) {
      todayMap[r.exercise_name] = r;
    }

    // Walk every exercise of the day so empty cells still get a draft
    // populated from history if one exists.
    for (const ej of day.ejercicios) {
      const todayRow = todayMap[ej.nombre];
      const historicalNote = latestNoteByExercise[ej.nombre] || '';
      const noteForUI = todayRow?.notes ?? historicalNote;
      state[ej.nombre] = {
        completed: !!todayRow?.completed,
        notes: noteForUI,
      };
      drafts[ej.nombre] = noteForUI;
    }
    setSessionState(state);
    setNoteDrafts(drafts);
  };

  const upsertSession = async (exerciseName: string, updates: Partial<SessionState>) => {
    if (!user) return;
    const current = sessionState[exerciseName] || { completed: false, notes: '' };
    const newState = { ...current, ...updates };
    const targetUserId = viewingUserId || user.id;

    setSessionState(prev => ({ ...prev, [exerciseName]: newState }));

    const payload = { completed: newState.completed, notes: newState.notes };

    const { data: existing, error: selectError } = await supabase
      .from('workout_session')
      .select('id')
      .eq('user_id', targetUserId)
      .eq('day_name', day.dia)
      .eq('exercise_name', exerciseName)
      .eq('session_date', today)
      .maybeSingle();

    if (selectError) return selectError;

    let error;
    if (existing) {
      ({ error } = await supabase
        .from('workout_session')
        .update(payload)
        .eq('id', existing.id));
    } else {
      ({ error } = await supabase
        .from('workout_session')
        .insert({ user_id: targetUserId, day_name: day.dia, exercise_name: exerciseName, session_date: today, ...payload }));
    }

    if (!error) {
      removeCachedData(`session_${targetUserId}_${day.dia}_${today}`);
    }

    return error;
  };

  const toggleCompleted = (exerciseName: string) => {
    if (!user) { toast.info('Inicia sesión para marcar ejercicios'); return; }
    const current = sessionState[exerciseName];
    const isCompleting = !current?.completed;
    upsertSession(exerciseName, { completed: isCompleting });

    if (!isCoachView) {
      const ej = day.ejercicios.find(e => e.nombre === exerciseName);
      if (ej?.equipmentType === 'cardio') {
        const cardio = parseCardioSpec(ej.weightSpec);
        if (isCompleting) {
          // Abrir dialog para que la cliente ingrese sus valores reales.
          // No insertar automáticamente con los valores del coach.
          setPendingCardio({
            ejercicioNombre: ej.nombre,
            defaultCals: cardio?.calorias ? Number(cardio.calorias) : 0,
            defaultDuration: cardio?.duracion || '',
            aparato: cardio?.aparato || ej.nombre,
          });
          setCardioRealCals(cardio?.calorias ? String(Number(cardio.calorias)) : '');
          setCardioRealDuration(cardio?.duracion || '');
        } else {
          (async () => {
            await supabase.from('exercise_logs')
              .delete()
              .eq('user_id', user.id)
              .eq('profile_name', activeUser)
              .eq('exercise_type', 'cardio')
              .eq('description', `[RUTINA] ${ej.nombre}`)
              .eq('log_date', today);
            onCardioLogged?.();
          })();
        }
      }
    }
  };

  const saveNote = async (exerciseName: string) => {
    if (!user) { toast.info('Inicia sesión para guardar notas'); return; }
    const noteText = noteDrafts[exerciseName] || '';
    const err = await upsertSession(exerciseName, { notes: noteText });
    if (!err) {
      setEditingNote(null);
      toast.success('Nota guardada');
    }
  };

  const deleteNote = async (exerciseName: string) => {
    if (!user) return;
    const targetUserId = viewingUserId || user.id;

    // Clear today's session record AND all historical notes for this
    // exercise on this routine day. Without this, the historical note
    // would re-appear tomorrow because the load logic falls back to the
    // most recent note when today's session has none.
    const err = await upsertSession(exerciseName, { notes: '' });
    if (!err) {
      await supabase
        .from('workout_session')
        .update({ notes: '' })
        .eq('user_id', targetUserId)
        .eq('day_name', day.dia)
        .eq('exercise_name', exerciseName);

      setNoteDrafts(prev => ({ ...prev, [exerciseName]: '' }));
      setSessionState(prev => ({
        ...prev,
        [exerciseName]: { ...(prev[exerciseName] || { completed: false, notes: '' }), notes: '' },
      }));
      setEditingNote(null);
      toast.success('Nota eliminada');
    }
  };

  const resetAll = async () => {
    if (!user) return;
    const exerciseNames = day.ejercicios.map(e => e.nombre);

    setSessionState(prev => {
      const newState = { ...prev };
      exerciseNames.forEach(name => {
        if (newState[name]) {
          newState[name] = { ...newState[name], completed: false };
        }
      });
      return newState;
    });
    toast.success('Rutina reiniciada (notas conservadas)');

    const updates = exerciseNames.map(name => {
      const current = sessionState[name];
      if (current?.completed) {
        return offlineMutation('workout_session', 'update',
          { user_id: user.id, day_name: day.dia, exercise_name: name, session_date: today, completed: false },
          () => supabase.from('workout_session').update({ completed: false })
            .eq('user_id', user.id)
            .eq('day_name', day.dia)
            .eq('exercise_name', name)
            .eq('session_date', today)
        );
      }
      return null;
    }).filter(Boolean);

    await Promise.all(updates);
  };

  const handleConfirmWorkout = async () => {
    if (!user || isCoachView) return;
    const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const insertData = {
      user_id: user.id,
      profile_name: activeUser,
      day_name: day.dia,
      exercises_count: totalCount,
      exercises_completed: completedCount,
      completion_percentage: pct,
      completion_date: today,
      completion_timestamp: new Date().toISOString(),
    };
    await offlineMutation(
      'routine_completion_logs', 'upsert', insertData,
      () => supabase.from('routine_completion_logs')
        .upsert(insertData, { onConflict: 'user_id,profile_name,day_name,completion_date' })
    );
    const key = `${day.dia}_${today}`;
    setConfirmedDays(prev => ({ ...prev, [key]: pct }));
    toast.success(pct === 100 ? '🏆 ¡Entrenamiento registrado!' : '💪 ¡Progreso registrado!');

    // Auto-notify the coach. Fire-and-forget — failures here must
    // never block the user's confirmation. CoachWorkoutFeed already
    // updates via realtime, this is just a push for when the coach
    // doesn't have the dashboard open.
    (async () => {
      try {
        // Notifica al coach PRINCIPAL (no al primer asignado). Antes
        // agarrábamos el más reciente y a veces era el coach-test en
        // lugar del coach real, así que Marco no recibía la noti.
        const coachId = await getPrimaryCoachId(user.id);
        if (!coachId) return;
        const emoji = pct === 100 ? '🏆' : pct >= 70 ? '💪' : '📝';
        await supabase.functions.invoke('send-push', {
          body: {
            user_ids: [coachId],
            title: `${emoji} ${activeUser} entrenó`,
            body: `${day.dia} · ${completedCount}/${totalCount} ejercicios (${pct}%)`,
            url: `/coach?client=${user.id}`,
          },
        });
      } catch { /* no-op */ }
    })();
  };

  const handleShareWhatsApp = async () => {
    // Construye mensaje formateado con info del entrenamiento
    const fecha = new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const emoji = pct === 100 ? '🏆' : pct >= 70 ? '💪' : '📝';
    const message = `${emoji} ¡Completé mi entrenamiento!\n\n📅 Fecha: ${fecha}\n📋 Día: ${day.dia}\n💪 Ejercicios: ${completedCount}/${totalCount} (${pct}%)\n👤 Cliente: ${activeUser}`;
    const whatsappUrl = 'https://chat.whatsapp.com/JHO3fHk50OdL3dB84G7sgE';

    // 1) Capturar el header del día CON el texto del mensaje embebido
    //    abajo, así toda la info viaja en un solo PNG aunque WhatsApp
    //    descarte el caption del share (común en iOS).
    let imageBlob: Blob | null = null;
    if (dayHeaderRef.current) {
      try {
        imageBlob = await captureWithMessage(dayHeaderRef.current, message);
      } catch (err) {
        console.error('Error al capturar screenshot:', err);
      }
    }

    // 2) Intentar Web Share API con la imagen (que ya contiene el texto
    //    embebido). Solo mandamos `files` para evitar el caption oculto
    //    que descartan algunas versiones de WhatsApp en iOS.
    if (imageBlob && typeof navigator.share === 'function') {
      const file = new File([imageBlob], `entrenamiento-${day.dia}-${today}.png`, { type: 'image/png' });
      try {
        // Algunos navegadores soportan navigator.share pero no archivos.
        // canShare nos deja saberlo antes y evita un error feo.
        const canShareFiles = typeof (navigator as any).canShare === 'function'
          ? (navigator as any).canShare({ files: [file] })
          : true;
        if (canShareFiles) {
          // Solo `files` — el mensaje ya está dentro de la imagen.
          await navigator.share({ files: [file] });
          return;
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return; // usuario canceló
        // Continúa al siguiente fallback si no soporta files
      }
    }

    // 3) Fallback: Web Share solo con texto (sin imagen) — sigue siendo nativo
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: message });
        return; // ✅ Compartido solo texto
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }

    // 4) Último recurso (desktop sin Web Share): copiar texto + abrir WhatsApp
    try {
      await navigator.clipboard.writeText(message);
      toast.success('📋 Mensaje copiado — pégalo en el grupo', { duration: 5000 });
    } catch {
      toast.info('Abriendo WhatsApp…');
    }

    setTimeout(() => {
      window.location.href = whatsappUrl;
    }, 800);
  };

  const formatWeight = (w: WeightLog) => {
    const modeLabel = w.weight_mode === 'por_lado' ? ' (por lado)' : '';
    return `${w.weight_value} ${w.weight_unit}${modeLabel}`;
  };

  const updateWeightsCache = (newWeights: Record<string, WeightLog[]>) => {
    if (user) setCachedData(`weights_${user.id}`, Object.values(newWeights).flat());
  };

  const addWeight = async (exerciseName: string) => {
    if (!user || !newWeight) return;
    const val = parseFloat(newWeight);
    if (isNaN(val)) { toast.error('Peso inválido'); return; }
    const insertData = {
      user_id: user.id,
      exercise_name: exerciseName,
      weight_value: val,
      weight_unit: newUnit,
      weight_mode: newMode,
      profile_name: activeUser,
    };

    const tempLog: WeightLog = {
      id: -Date.now(),
      exercise_name: exerciseName,
      weight_value: val,
      weight_unit: newUnit,
      weight_mode: newMode,
      log_date: today,
      notes: null,
    };
    setWeights(prev => {
      const updated = { ...prev, [exerciseName]: [tempLog, ...(prev[exerciseName] || [])] };
      updateWeightsCache(updated);
      return updated;
    });

    toast.success(`${val} ${newUnit}${newMode === 'por_lado' ? ' (por lado)' : ''} registrado`);
    setNewWeight('');
    setNewUnit('Lbs');
    setNewMode('total');
    setAddingFor(null);

    await offlineMutation('exercise_weights', 'insert', insertData, () =>
      supabase.from('exercise_weights').insert(insertData)
    );
  };

  const updateWeight = async (id: number) => {
    if (!user) return;
    const val = parseFloat(editWeightValue);
    if (isNaN(val)) { toast.error('Peso inválido'); return; }

    setWeights(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = updated[key].map(w =>
          w.id === id ? { ...w, weight_value: val, weight_unit: editWeightUnit, weight_mode: editWeightMode } : w
        );
      }
      updateWeightsCache(updated);
      return updated;
    });
    toast.success('Peso actualizado');
    setEditingWeightId(null);

    await offlineMutation('exercise_weights', 'update',
      { id, weight_value: val, weight_unit: editWeightUnit, weight_mode: editWeightMode },
      () => supabase.from('exercise_weights').update({
        weight_value: val, weight_unit: editWeightUnit, weight_mode: editWeightMode
      }).eq('id', id)
    );
  };

  const deleteWeight = async (id: number) => {
    if (!user) return;

    setWeights(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = updated[key].filter(w => w.id !== id);
      }
      updateWeightsCache(updated);
      return updated;
    });
    toast.success('Registro eliminado');

    await offlineMutation('exercise_weights', 'delete', { id }, () =>
      supabase.from('exercise_weights').delete().eq('id', id), ['id']
    );
  };

  const getLatestWeight = (name: string) => weights[name]?.[0];
  const getPreviousWeight = (name: string) => weights[name]?.[1];

  /**
   * Decide si el texto de "peso sugerido por el coach" tiene info
   * útil para mostrar al cliente. Devuelve false cuando solo duplica
   * series/reps (que ya aparecen arriba en el header), evitando
   * tarjetas redundantes tipo "3 series de 12-15 repeticiones" debajo
   * de un header que ya dice "3 series × 12-15 reps".
   *
   * Mantiene visible:
   *   - Cualquier texto con unidad de peso (kg, lb, lbs, placas, discos)
   *   - Notas con matices (ej. "Agregar 10 pulsos al final", "al fallo")
   *   - JSON de cardio (lo procesa parseCardioSpec)
   *
   * Oculta:
   *   - Vacíos, "—", "-", "N/A"
   *   - "X series × Y reps" puro sin matices
   */
  const isInformativeCoachWeight = (text: string | undefined | null): boolean => {
    if (!text) return false;
    const t = text.trim();
    if (!t || /^[-—–]+$/.test(t) || /^n\/a$/i.test(t) || t === '--') return false;
    // Cardio JSON / cualquier estructura → dejar pasar (lo maneja parseCardioSpec)
    if (t.startsWith('{')) return true;
    // Tiene unidad de peso → útil
    if (/\b(kg|lb|lbs|placa|placas|disco|discos)\b/i.test(t)) return true;
    // Quitar patrones "X series × Y reps" / "X sets de Y" y ver qué queda
    const cleaned = t
      .replace(/\d+\s*(series|sets?)\s*(?:[×x·de]\s*)?\d+(?:\s*[-–]\s*\d+)?\s*(reps?|repetic\w*)?/gi, '')
      .replace(/\b(series|sets?|reps?|repetic\w*)\b/gi, '')
      .trim();
    // Si quedan ≥3 letras → hay matiz (ej. "pulsos", "al fallo", "x pierna")
    return /[a-zñáéíóú]{3,}/i.test(cleaned);
  };

  const parseBaseWeight = (pesoCoach: string): number | null => {
    const matches = pesoCoach.match(/(\d+(?:\.\d+)?)\s*(?:kg|lbs|lb)/gi);
    if (matches && matches.length > 0) {
      const last = matches[matches.length - 1];
      const num = last.match(/(\d+(?:\.\d+)?)/);
      return num ? parseFloat(num[1]) : null;
    }
    const allNums = pesoCoach.match(/(\d+(?:\.\d+)?)/g);
    return allNums ? parseFloat(allNums[allNums.length - 1]) : null;
  };

  const getWeightColorClass = (currentWeight: number | undefined, baseWeight: string): string => {
    if (currentWeight === undefined) return 'text-accent';
    const base = parseBaseWeight(baseWeight);
    if (base === null) return 'text-accent';
    if (currentWeight > base) return 'text-green-500';
    if (currentWeight < base) return 'text-red-500';
    return 'text-yellow-500';
  };

  const pendingExercises = day.ejercicios.filter(ej => !sessionState[ej.nombre]?.completed);
  const completedExercises = day.ejercicios.filter(ej => sessionState[ej.nombre]?.completed);
  const completedCount = completedExercises.length;
  const totalCount = day.ejercicios.length;

  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
    return match?.[1] ?? null;
  };

  const getVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match?.[1] ?? null;
  };

  const openVideo = async (url: string, type: string, name: string) => {
    setSignedVideoUrl(null);
    setVideoPlayer({ url, type, name });
    if (type === 'supabase') {
      const { data } = await supabase.storage.from('exercise-demos').createSignedUrl(url, 3600);
      setSignedVideoUrl(data?.signedUrl ?? null);
    }
  };

  const renderExercise = (ej: typeof day.ejercicios[0], originalIndex: number, isCompleted: boolean) => {
    const latest = getLatestWeight(ej.nombre);
    const history = weights[ej.nombre] || [];
    const isAdding = addingFor === ej.nombre;
    const isShowingHistory = showHistoryFor === ej.nombre;
    const savedNote = sessionState[ej.nombre]?.notes || '';
    const isEditingThisNote = editingNote === ej.nombre;
    const draft = noteDrafts[ej.nombre] ?? '';

    // Hero image source — three-step fallback chain:
    //   1. coach-uploaded image (per-exercise image_url in client_routines)
    //   2. hardcoded mapping for the 217 global catalog entries
    //   3. auto-extracted YouTube thumbnail when the demo video is YT
    // Anything else (Vimeo without image, no media at all) → no hero,
    // card stays compact.
    let heroImage: string | null = ej.imageUrl ?? getExerciseImage(ej.nombre) ?? null;
    // Cascada de fallback resolutions para thumbnails de YouTube:
    // maxres → hq → mq → sd → default. Si el video no tiene
    // maxresdefault (común en videos antiguos), cae al siguiente
    // automáticamente sin dejar el card vacío.
    let heroImageFallbacks: string[] = [];
    if (!heroImage && ej.demoMediaUrl && (ej.demoMediaType === 'youtube' || !ej.demoMediaType)) {
      const fallbacks = youtubeThumbnailFallbacks(ej.demoMediaUrl);
      if (fallbacks.length > 0) {
        heroImage = fallbacks[0];
        heroImageFallbacks = fallbacks.slice(1);
      } else {
        heroImage = youtubeThumbnail(ej.demoMediaUrl);
      }
    }
    const hasVideo = !!ej.demoMediaUrl;
    const handleHeroClick = () => {
      if (hasVideo) {
        openVideo(ej.demoMediaUrl!, ej.demoMediaType || 'youtube', ej.nombre);
      } else if (heroImage) {
        setZoomedImage(heroImage);
      }
    };

    return (
      <div
        key={ej.nombre}
        className={`bg-card border-2 rounded-xl overflow-hidden transition-all ${
          isCompleted
            ? 'border-accent/30 opacity-60'
            : 'border-secondary hover:border-accent/50'
        }`}
      >
        {/* HERO — same structure for every exercise so the routine
            list reads as a uniform grid. Three render modes:
            (a) image present → shown as 16:9 cover, optional ▶ overlay
                if there's a video tutorial behind it.
            (b) no image but video present → dark placeholder with
                Dumbbell icon + ▶ overlay. Same height as (a) so the
                vertical rhythm of the list doesn't break.
            (c) no image and no video → no hero at all (compact card).
            Trainerize/TrueCoach/MyPTHub all keep heroes uniform. */}
        {(heroImage || hasVideo) && (
          <button
            onClick={handleHeroClick}
            disabled={!hasVideo && !heroImage}
            className={`relative w-full block bg-secondary/40 group ${isCompleted ? 'grayscale' : ''}`}
            style={{ aspectRatio: '16 / 9' }}
            aria-label={hasVideo ? `Ver video de ${ej.nombre}` : `Ver imagen de ${ej.nombre}`}
          >
            {heroImage ? (
              // ResilientImage reintenta cargar al volver online y
              // muestra un indicador "sin conexión" cuando falla
              // offline. Antes hacíamos display:none al fallar y el
              // usuario veía un fondo gris ambiguo (¿no hay video?
              // ¿estoy offline? ¿bug?). Ahora el fallback es el
              // mismo gradiente con la pesa, más el ícono amber
              // que indica explícitamente que es por falta de red.
              <ResilientImage
                src={heroImage}
                fallbackSrcs={heroImageFallbacks}
                alt={ej.nombre}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
                loading="lazy"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary/60 to-secondary/30">
                    <Dumbbell className="w-10 h-10 text-muted-foreground/40" />
                  </div>
                }
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary/60 to-secondary/30">
                <Dumbbell className="w-10 h-10 text-muted-foreground/40" />
              </div>
            )}
            {hasVideo && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl">
                  <Video className="w-6 h-6 md:w-7 md:h-7 text-destructive translate-x-[1px]" fill="currentColor" />
                </span>
              </span>
            )}
            {!hasVideo && heroImage && (
              <span className="absolute top-2 right-2 bg-black/40 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-3.5 h-3.5 text-white" />
              </span>
            )}
          </button>
        )}

        <div className="p-3 md:p-5">
          {/* Header — checkbox + name */}
          <div className="flex items-start gap-2 md:gap-4 min-w-0">
            <button
              onClick={() => !isCoachView && toggleCompleted(ej.nombre)}
              disabled={isCoachView}
              className={`mt-0.5 shrink-0 w-8 h-8 min-w-[44px] min-h-[44px] md:w-6 md:h-6 md:min-w-0 md:min-h-0 rounded-md border-2 flex items-center justify-center transition-all ${
                isCompleted
                  ? 'bg-accent border-accent text-primary'
                  : 'border-muted-foreground/40 hover:border-accent'
              } ${isCoachView ? 'cursor-default opacity-70' : ''}`}
            >
              {isCompleted && <Check size={14} strokeWidth={3} />}
            </button>
            <div className="min-w-0 flex-1">
              <h4 className={`font-black text-xs md:text-sm uppercase tracking-tight ${isCompleted ? 'line-through text-muted-foreground' : ''} break-words flex items-start gap-2`}>
                <span
                  className={`shrink-0 inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-md text-[11px] font-black ${
                    isCompleted
                      ? 'bg-secondary text-muted-foreground'
                      : 'bg-accent text-accent-foreground'
                  }`}
                  aria-label={`Ejercicio ${originalIndex + 1}`}
                >
                  {originalIndex + 1}
                </span>
                <span className="flex-1">{ej.nombre}</span>
              </h4>
              {/* Reps + Equipo en la misma línea (cuando ambos están)
               *  para liberar espacio vertical y que la card de
               *  tempo abajo se vea balanceada en su rectángulo. */}
              {(ej.series || ej.reps || (ej.equipmentType && ej.equipmentType !== 'otro')) && (
                <p className="text-[11px] font-black text-foreground/80 mt-0.5 tracking-wide flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  {(ej.series || ej.reps) && (
                    <span>
                      {ej.series && ej.reps
                        ? `${ej.series} series × ${ej.reps} reps`
                        : ej.series
                          ? `${ej.series} series`
                          : `${ej.reps} reps`}
                    </span>
                  )}
                  {ej.equipmentType && ej.equipmentType !== 'otro' && (
                    <span className="text-[10px] text-accent font-bold uppercase tracking-wider">
                      {(ej.series || ej.reps) && <span className="text-muted-foreground/50 mr-1">·</span>}
                      {ej.equipmentType === 'barra_libre' ? '🏋️ Barra libre' :
                       ej.equipmentType === 'smith' ? '🔩 Smith' :
                       ej.equipmentType === 'mancuernas' ? '💪 Mancuernas' :
                       ej.equipmentType === 'maquina_lbs' ? '⚙️ Máquina (lbs)' :
                       ej.equipmentType === 'maquina_discos' ? '🔧 Máquina (discos)' :
                       ej.equipmentType === 'polea_lbs' ? '🔗 Polea (lbs)' :
                       ej.equipmentType === 'polea_barras' ? '🔗 Polea (barras)' :
                       ej.equipmentType === 'peso_corporal' ? '🤸 Peso corporal' :
                       ej.equipmentType === 'cardio' ? '🔥 Cardio' : ''}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Tempo display — FUERA del flex header para que se alinee
           *  con el resto del contenido (peso base, peso actual,
           *  notas) y NO herede el offset del checkbox del header.
           *
           *  Antes estaba dentro de la columna derecha del flex y
           *  quedaba indentado ~52px (ancho del checkbox) → se veía
           *  cargado a la derecha vs "PESO BASE (COACH)" que empieza
           *  desde el borde izquierdo. */}
          <div className="pl-0 md:pl-12">
            <TempoDisplay
              eccentrica={ej.tempoEccentrica}
              pausaAbajo={ej.tempoPausaAbajo}
              concentrica={ej.tempoConcentrica}
              pausaArriba={ej.tempoPausaArriba}
              code={ej.isometria}
            />
          </div>

        {/* Stats
         *
         *  La columna "Peso Sugerido (Coach)" se oculta cuando el coach
         *  no llenó peso real — series y reps ya se muestran arriba en
         *  el header del ejercicio, así que repetir "3 series × 8-12
         *  reps" debajo solo confunde. Ver isInformativeCoachWeight().
         *  Si se oculta, "Peso Actual" se expande a ancho completo.
         */}
        {(() => {
          const cardioSpec = ej.weightSpec ? parseCardioSpec(ej.weightSpec) : null;
          const coachText = ej.weightSpec || ej.pesoCoach;
          const hasCoachSuggestion = !!cardioSpec || isInformativeCoachWeight(coachText);
          const showActual = ej.equipmentType !== 'cardio';
          const gridCols = ej.equipmentType === 'cardio'
            ? 'grid-cols-1'
            : hasCoachSuggestion
              ? 'grid-cols-1 sm:grid-cols-3'
              : 'grid-cols-1';
          return (
        <div className={`grid gap-3 md:gap-4 mt-3 md:mt-4 pl-0 md:pl-12 ${gridCols}`}>
          {hasCoachSuggestion && (
          <div className={ej.equipmentType === 'cardio' ? '' : 'sm:col-span-2'}>
            {cardioSpec ? (
                <div className="space-y-1 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-700/40 rounded-lg p-3">
                  <p className="text-[10px] font-bold text-green-700 dark:text-green-300 uppercase tracking-wider mb-2">🔥 Cardio</p>
                  {cardioSpec.aparato && <p className="text-xs font-bold text-green-800 dark:text-green-200">{cardioSpec.aparato}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-green-700 dark:text-green-300">
                    {cardioSpec.duracion && <span>⏱ <strong>{cardioSpec.duracion} min</strong></span>}
                    {cardioSpec.calorias && <span>🔥 <strong>{cardioSpec.calorias} kcal</strong></span>}
                    {cardioSpec.velocidad && <span>🏃 <strong>{cardioSpec.velocidad}</strong></span>}
                    {cardioSpec.inclinacion && <span>📐 <strong>{cardioSpec.inclinacion}</strong></span>}
                  </div>
                </div>
              ) : ej.weightSpec ? (
                <>
                  <span className="fitness-label block mb-1">Peso Sugerido (Coach)</span>
                  <pre className="text-xs font-medium whitespace-pre-wrap bg-secondary/50 rounded-lg p-2 leading-relaxed">{ej.weightSpec}</pre>
                </>
              ) : (
              <>
                <span className="fitness-label block mb-1">Peso Sugerido (Coach)</span>
                <span className="text-sm font-bold">{ej.pesoCoach}</span>
              </>
            )}
          </div>
          )}
          {showActual && (<div>
            <span className="fitness-label block mb-1">Peso Actual</span>
            <span className={`text-sm font-bold ${getWeightColorClass(latest?.weight_value, ej.pesoCoach)}`}>
              {latest ? formatWeight(latest) : ej.pesoReal}
            </span>
            {getPreviousWeight(ej.nombre) && (
              <>
                <span className="fitness-label block mb-1 mt-2">Peso Anterior</span>
                <span className={`text-xs font-bold ${(() => {
                  const prev = getPreviousWeight(ej.nombre);
                  if (!prev) return 'text-muted-foreground';
                  const current = latest?.weight_value;
                  if (current === undefined) return 'text-muted-foreground';
                  if (prev.weight_value > current) return 'text-green-500';
                  if (prev.weight_value < current) return 'text-red-500';
                  return 'text-yellow-500';
                })()}`}>
                  {(() => {
                    const prev = getPreviousWeight(ej.nombre);
                    return prev ? `${formatWeight(prev)} — ${prev.log_date}` : '—';
                  })()}
                </span>
              </>
            )}
          </div>)}
        </div>
          );
        })()}

        {ej.comentarios && (() => {
          const lines = ej.comentarios.split('\n');
          const hasMore = lines.length > 3 || ej.comentarios.length > 120;
          const expanded = expandedComments.has(ej.nombre);
          return (
            <div className="mt-3 pl-0 md:pl-12">
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-500/30 rounded-lg p-3">
                <div className="flex items-start gap-1.5">
                  <span className="text-amber-600 shrink-0 mt-0.5 text-sm">💡</span>
                  <p className={`text-xs text-amber-800 dark:text-amber-200 whitespace-pre-line leading-relaxed flex-1 ${!expanded ? 'line-clamp-3' : ''}`}>
                    {ej.comentarios}
                  </p>
                </div>
                {hasMore && (
                  <button
                    onClick={() => setExpandedComments(prev => {
                      const next = new Set(prev);
                      next.has(ej.nombre) ? next.delete(ej.nombre) : next.add(ej.nombre);
                      return next;
                    })}
                    className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
                  >
                    {expanded
                      ? <><ChevronUp className="h-3 w-3" /> Ver menos</>
                      : <><ChevronDown className="h-3 w-3" /> Ver más</>}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Notes section */}
        <div className="mt-3 pl-0 md:pl-12">
          {savedNote && (!isEditingThisNote || isCoachView) ? (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-foreground whitespace-pre-wrap flex-1">📝 {savedNote}</p>
              {!isCoachView && (
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => { setEditingNote(ej.nombre); setNoteDrafts(prev => ({ ...prev, [ej.nombre]: savedNote })); }}
                    className="text-muted-foreground hover:text-accent transition-colors p-1"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => setNoteToDelete(ej.nombre)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ) : !isCoachView ? (
            <div className="flex gap-2 items-start">
              <Textarea
                placeholder="Tu nota personal para este ejercicio (ej: recordatorio, objetivo, sensaciones...)"
                value={draft}
                onChange={e => setNoteDrafts(prev => ({ ...prev, [ej.nombre]: e.target.value }))}
                className="min-h-[36px] h-9 text-xs resize-none bg-secondary/30 border-border/50 focus:bg-background flex-1"
                rows={1}
              />
              <Button
                size="sm"
                variant="outline"
                className="text-xs font-bold shrink-0"
                onClick={() => saveNote(ej.nombre)}
                disabled={!draft.trim()}
              >
                <Save size={12} /> Guardar
              </Button>
              {isEditingThisNote && (
                <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setEditingNote(null)}>
                  <X size={14} />
                </Button>
              )}
            </div>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mt-3 pl-0 md:pl-12">
          {ej.equipmentType !== 'cardio' && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs font-bold uppercase min-h-[44px] border-accent/30 text-accent hover:bg-accent/10"
              onClick={() => openTimer(ej.restOptions, ej.nombre)}
            >
              <Timer size={14} /> Descanso
              {ej.restOptions && ej.restOptions.length > 0 && (
                <span className="ml-1 text-[10px] opacity-70">
                  ({ej.restOptions.map(o => o === '0.5' ? '30s' : o === '1' ? '1m' : `${o}m`).join('/')})
                </span>
              )}
            </Button>
          )}
          {!isCoachView && ej.equipmentType !== 'cardio' && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs font-bold uppercase min-h-[44px]"
              onClick={() => {
                if (!user) { toast.info('Inicia sesión para registrar pesos'); return; }
                setAddingFor(isAdding ? null : ej.nombre);
                setShowHistoryFor(null);
              }}
            >
              <Plus size={14} /> Agregar Peso
            </Button>
          )}
          {history.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs font-bold uppercase min-h-[44px]"
              onClick={() => { setShowHistoryFor(isShowingHistory ? null : ej.nombre); setAddingFor(null); }}
            >
              <History size={14} /> Historial ({history.length})
            </Button>
          )}
        </div>

        {/* Add weight form */}
        {isAdding && (
          <div className="mt-3 pl-0 md:pl-12 flex gap-2 items-center flex-wrap">
            <Input
              type="number"
              placeholder="Peso"
              value={newWeight}
              onChange={e => setNewWeight(e.target.value)}
              className="w-24 h-9 text-sm"
              onKeyDown={e => e.key === 'Enter' && addWeight(ej.nombre)}
            />
            <Select value={newUnit} onValueChange={(v) => setNewUnit(v as WeightUnit)}>
              <SelectTrigger className="w-24 h-9 text-xs font-bold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Lbs">Lbs</SelectItem>
                <SelectItem value="Kg">Kg</SelectItem>
                <SelectItem value="Placas">Placas</SelectItem>
                <SelectItem value="Discos">Discos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={newMode} onValueChange={(v) => setNewMode(v as WeightMode)}>
              <SelectTrigger className="w-28 h-9 text-xs font-bold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total">Peso Total</SelectItem>
                <SelectItem value="por_lado">Por Lado</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => addWeight(ej.nombre)} className="text-xs font-bold">
              Guardar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingFor(null)}>
              <X size={14} />
            </Button>
          </div>
        )}

        {/* Weight history */}
        {isShowingHistory && (
          <div className="mt-3 pl-0 md:pl-12 bg-secondary/50 rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <span className="fitness-label">Historial de Pesos</span>
              <button onClick={() => setShowHistoryFor(null)} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            </div>
            <div className="space-y-1">
              {history.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/50 last:border-0 gap-2">
                  {editingWeightId === w.id ? (
                    <>
                      <div className="flex gap-1 items-center flex-1 flex-wrap">
                        <Input
                          type="number"
                          value={editWeightValue}
                          onChange={e => setEditWeightValue(e.target.value)}
                          className="w-20 h-7 text-xs"
                          onKeyDown={e => e.key === 'Enter' && updateWeight(w.id)}
                        />
                        <Select value={editWeightUnit} onValueChange={(v) => setEditWeightUnit(v as WeightUnit)}>
                          <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Lbs">Lbs</SelectItem>
                            <SelectItem value="Kg">Kg</SelectItem>
                            <SelectItem value="Placas">Placas</SelectItem>
                            <SelectItem value="Discos">Discos</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={editWeightMode} onValueChange={(v) => setEditWeightMode(v as WeightMode)}>
                          <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="total">Total</SelectItem>
                            <SelectItem value="por_lado">Por Lado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => updateWeight(w.id)} className="text-accent hover:text-accent/80 p-1"><Check size={12} /></button>
                        <button onClick={() => setEditingWeightId(null)} className="text-muted-foreground hover:text-foreground p-1"><X size={12} /></button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="font-bold">{formatWeight(w)}</span>
                      <span className="text-muted-foreground flex-1 text-right">{w.log_date}</span>
                      {!isCoachView && (
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() => {
                              setEditingWeightId(w.id);
                              setEditWeightValue(String(w.weight_value));
                              setEditWeightUnit(w.weight_unit as WeightUnit);
                              setEditWeightMode((w.weight_mode || 'total') as WeightMode);
                            }}
                            className="text-muted-foreground hover:text-accent p-1"
                          >
                            <Pencil size={11} />
                          </button>
                          <button onClick={() => deleteWeight(w.id)} className="text-muted-foreground hover:text-destructive p-1">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    );
  };

  return (
    <section id="rutina" className="scroll-mt-32">
      <SectionHeader title="Rutina" subtitle={subtitle} icon={Dumbbell} helpId="rutina" />

      {/* STICKY day controls — visible while the user scrolls through
          the exercise list. Coach feedback: at the gym, after 5 minutes
          of scrolling exercises, you forget which day / muscle group
          you are looking at. This bar keeps that context glued at the
          top so they can switch days without scrolling all the way
          back, and always see grupo + progreso while training. */}
      <div className="sticky top-[60px] z-30 bg-background/95 backdrop-blur-sm border-y border-border -mx-3 md:mx-0 px-3 md:px-4 py-2.5 mb-4 md:mb-6 space-y-2">
        {/* Section title row — keeps "RUTINA" visible while scrolling so the
            user always knows which area of the app they are in. */}
        <div className="flex items-center gap-1.5 text-accent">
          <Dumbbell className="w-3.5 h-3.5 shrink-0" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em]">
            Rutina · {subtitle}
          </span>
        </div>

        {/* Day selector — compact chips */}
        <ScrollHintRow className="-mx-1 px-1">
        <div className="flex gap-1.5">
          {rutina.map((d, i) => (
            <button
              key={d.dia}
              onClick={() => { setSelectedDay(i); setAddingFor(null); setShowHistoryFor(null); }}
              className={`px-3 py-2 rounded-lg font-black uppercase tracking-wider text-[11px] whitespace-nowrap transition-all min-h-[40px] shrink-0 ${
                selectedDay === i
                  ? 'bg-primary text-accent shadow-lg'
                  : 'bg-secondary text-muted-foreground hover:bg-border'
              }`}
            >
              {d.dia}
            </button>
          ))}
        </div>
        </ScrollHintRow>

        {/* Compact day info: muscle group + progress */}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-black uppercase tracking-tight truncate">
              {day.grupo}
            </div>
            {day.cardio && (
              <div className="text-[10px] text-accent font-bold uppercase truncate">
                + Cardio: {day.cardio}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-16 sm:w-24 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-500"
                style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
            <span className="text-xs font-black text-accent whitespace-nowrap">
              {completedCount}/{totalCount}
            </span>
          </div>
        </div>
      </div>

      {/* Day header + reset */}
      <div ref={dayHeaderRef} className="bg-primary text-primary-foreground rounded-xl md:rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <div>
            <h3 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">{day.grupo}</h3>
            <p className="text-xs text-primary-foreground/60 font-bold uppercase tracking-widest mt-1">
              {day.dia} — {activeUser}
            </p>
          </div>
          <div className="flex gap-3 md:gap-4 items-center flex-wrap">
            {day.cardio && (
              <div className="flex items-center gap-2 text-accent">
                <Flame size={16} />
                <span className="text-xs font-black uppercase">Cardio: {day.cardio}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              {completedCount === 0 && (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary-foreground/50 rounded-sm shrink-0" />
                  <span className="text-xs font-black uppercase text-primary-foreground/60">Marca cada ejercicio al completarlo</span>
                </>
              )}
              {completedCount > 0 && completedCount < totalCount && (
                <>
                  <Check size={14} className="text-accent shrink-0" />
                  <span className="text-xs font-black uppercase text-accent">{completedCount}/{totalCount} ejercicios completados</span>
                </>
              )}
              {completedCount === totalCount && totalCount > 0 && (
                <>
                  <Check size={14} className="text-green-400 shrink-0" />
                  <span className="text-xs font-black uppercase text-green-400">¡Día completado!</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar + reset */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex-1 bg-primary-foreground/10 rounded-full h-2 overflow-hidden">
            <div
              className="bg-accent h-full rounded-full transition-all duration-500"
              style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs font-black text-accent">{completedCount}/{totalCount}</span>

          {completedCount > 0 && !isCoachView && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-xs font-bold uppercase text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10">
                  <RotateCcw size={14} /> Reiniciar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Reiniciar rutina?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Todos los ejercicios de {day.dia} volverán a estado pendiente. Las notas se conservarán.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={resetAll}>Reiniciar Todo</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Confirmation dialog for permanently deleting a note */}
        <AlertDialog
          open={noteToDelete !== null}
          onOpenChange={(open) => { if (!open) setNoteToDelete(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar la nota permanentemente?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción borra la nota de hoy y de todos los días anteriores donde la guardaste.
                <span className="block mt-2 font-semibold text-destructive">
                  Esta acción no se puede deshacer.
                </span>
                Si solo quieres editarla, mejor abre la nota y reemplaza el texto.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (noteToDelete) deleteNote(noteToDelete);
                  setNoteToDelete(null);
                }}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                Sí, eliminar para siempre
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Dialog: cardio real (tiempo + calorías del cliente) ── */}
        <AlertDialog
          open={!!pendingCardio}
          onOpenChange={open => {
            if (!open) {
              // Si la cierra sin confirmar, desmarcar el check del ejercicio
              if (pendingCardio) {
                upsertSession(pendingCardio.ejercicioNombre, { completed: false });
              }
              setPendingCardio(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Cuánto cardio hiciste?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4 pt-1">
                  <p className="text-sm text-muted-foreground">
                    El coach indicó <strong>{pendingCardio?.defaultCals} kcal</strong>
                    {pendingCardio?.defaultDuration ? ` · ${pendingCardio.defaultDuration} min` : ''}. Ajusta según lo que realmente hiciste.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Calorías reales
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        className="fitness-input"
                        value={cardioRealCals}
                        onChange={e => setCardioRealCals(e.target.value)}
                        placeholder={String(pendingCardio?.defaultCals ?? '')}
                        autoFocus
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Tiempo (min)
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        className="fitness-input"
                        value={cardioRealDuration}
                        onChange={e => setCardioRealDuration(e.target.value)}
                        placeholder={pendingCardio?.defaultDuration || '—'}
                      />
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                disabled={!cardioRealCals || Number(cardioRealCals) <= 0}
                onClick={async () => {
                  if (!pendingCardio || !user) return;
                  const cals = Number(cardioRealCals);
                  const dur = cardioRealDuration ? Number(cardioRealDuration) : null;
                  const aparatoPart = pendingCardio.aparato ? ` · ${pendingCardio.aparato}` : '';
                  const durPart = dur ? ` · ${dur} min` : '';
                  const insertData: Record<string, unknown> = {
                    user_id: user.id,
                    profile_name: activeUser,
                    exercise_type: 'cardio',
                    description: `[RUTINA] ${pendingCardio.ejercicioNombre}`,
                    calories_burned: cals,
                    log_date: today,
                  };
                  if (dur) insertData.duration_minutes = dur;
                  await offlineMutation('exercise_logs', 'insert', insertData, () =>
                    supabase.from('exercise_logs').insert(insertData)
                  );
                  setPendingCardio(null);
                  onCardioLogged?.();
                }}
              >
                Guardar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Botones de confirmación / actualización / WhatsApp ── */}
        {!isCoachView && (() => {
          const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
          const confirmKey = `${day.dia}_${today}`;
          const registeredPct = confirmedDays[confirmKey]; // undefined = nunca registrado
          const isRegistered = registeredPct !== undefined;
          const canUpdate = isRegistered && pct > registeredPct; // hay mejora respecto al registrado

          // Si nunca registró y no llegó al 50%, no hay nada que mostrar
          if (!isRegistered && pct < 50) return null;
          // Si nunca registró aún, mostrar solo botón de registrar
          // Si ya registró, mostrar siempre WhatsApp + (opcional) botón de actualizar

          return (
            <div className="mt-4 space-y-2">
              {!isRegistered && (
                <>
                  <p className="text-[10px] text-primary-foreground/50 uppercase tracking-widest font-bold text-center">
                    Tu coach no verá tu entrenamiento hasta que lo registres
                  </p>
                  <button
                    onClick={handleConfirmWorkout}
                    className={`w-full flex items-center justify-center gap-2 min-h-[44px] rounded-xl font-black text-xs uppercase tracking-widest transition-all ${
                      pct === 100
                        ? 'bg-green-500 hover:bg-green-600 text-white shadow-md'
                        : 'bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground border border-primary-foreground/20'
                    }`}
                  >
                    📋 {pct === 100 ? 'Registrar entrenamiento de hoy'
                        : pct >= 70  ? 'Registrar mi progreso de hoy'
                                     : 'Registrar progreso parcial de hoy'}
                  </button>
                </>
              )}

              {isRegistered && (
                <>
                  <p className="text-[10px] text-green-400/80 uppercase tracking-widest font-bold text-center">
                    ✅ Registrado al {registeredPct}% — envía evidencia a tu coach
                  </p>

                  {canUpdate && (
                    <button
                      onClick={handleConfirmWorkout}
                      className="w-full flex items-center justify-center gap-2 min-h-[44px] rounded-xl font-black text-xs uppercase tracking-widest transition-all bg-amber-500 hover:bg-amber-600 text-white shadow-md"
                    >
                      🔄 Actualizar registro al {pct}% (antes {registeredPct}%)
                    </button>
                  )}

                  <button
                    onClick={handleShareWhatsApp}
                    className="w-full flex items-center justify-center gap-2 min-h-[44px] rounded-xl font-black text-xs uppercase tracking-widest transition-all bg-[#25D366] hover:bg-[#1ebe5b] text-white shadow-sm"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current shrink-0" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Enviar evidencia al coach por WhatsApp
                  </button>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Pending exercises */}
      {pendingExercises.length > 0 && (
        <div className="space-y-3">
          {completedCount > 0 && (
            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2">
              ⏳ Pendientes ({pendingExercises.length})
            </p>
          )}
          {pendingExercises.map((ej) => {
            const idx = day.ejercicios.indexOf(ej);
            return renderExercise(ej, idx, false);
          })}
        </div>
      )}

      {/* Completed exercises */}
      {completedExercises.length > 0 && (
        <div className="space-y-3 mt-6">
          <p className="text-xs font-black uppercase tracking-widest text-accent flex items-center gap-2">
            <Check size={14} /> Terminados ({completedExercises.length})
          </p>
          {completedExercises.map((ej) => {
            const idx = day.ejercicios.indexOf(ej);
            return renderExercise(ej, idx, true);
          })}
        </div>
      )}

      <Dialog open={!!zoomedImage} onOpenChange={() => setZoomedImage(null)}>
        <DialogContent className="max-w-md p-2">
          <DialogTitle className="sr-only">Referencia del ejercicio</DialogTitle>
          {zoomedImage && (
            <ResilientImage
              src={zoomedImage}
              alt="Referencia del ejercicio"
              className="w-full rounded-lg"
              fallback={
                <div className="text-foreground p-8 text-center bg-muted/50 rounded-lg">
                  No se pudo cargar la imagen del ejercicio.
                </div>
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!videoPlayer} onOpenChange={open => { if (!open) { setVideoPlayer(null); setSignedVideoUrl(null); } }}>
        <DialogContent className="max-w-lg p-3">
          <DialogTitle className="text-sm font-black uppercase tracking-tight flex items-center gap-2">
            <Video size={14} className="text-blue-600" /> {videoPlayer?.name}
          </DialogTitle>
          {videoPlayer && (
            <div className="mt-2 rounded-lg overflow-hidden bg-black aspect-video w-full">
              {(videoPlayer.type === 'youtube') && (() => {
                const id = getYouTubeId(videoPlayer.url);
                return id ? (
                  <VideoIframeWithFallback
                    embedSrc={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
                    externalUrl={videoPlayer.url}
                    label="YouTube"
                  />
                ) : <p className="text-white text-xs p-4">No se pudo cargar el video.</p>;
              })()}
              {videoPlayer.type === 'vimeo' && (() => {
                const id = getVimeoId(videoPlayer.url);
                return id ? (
                  <VideoIframeWithFallback
                    embedSrc={`https://player.vimeo.com/video/${id}?autoplay=1`}
                    externalUrl={videoPlayer.url}
                    label="Vimeo"
                  />
                ) : <p className="text-white text-xs p-4">No se pudo cargar el video.</p>;
              })()}
              {videoPlayer.type === 'instagram' && (
                <div className="flex items-center justify-center h-full">
                  <a href={videoPlayer.url} target="_blank" rel="noopener noreferrer"
                    className="text-blue-400 underline text-sm">
                    Ver en Instagram
                  </a>
                </div>
              )}
              {videoPlayer.type === 'supabase' && (
                signedVideoUrl
                  ? <video src={signedVideoUrl} controls autoPlay className="w-full h-full" />
                  : <div className="flex items-center justify-center h-full text-white text-xs">Cargando video...</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RestTimer modal vive globalmente en App.tsx — solo
       *  disparamos openModal desde aquí. Esto permite que el timer
       *  siga corriendo aunque el cliente navegue fuera de la
       *  rutina (al heatmap, plan, etc.) */}
    </section>
  );
}

/**
 * Wrapper de <iframe> que detecta cuando el video no cargó en N segundos
 * y muestra un escape claro al usuario: "Abrir en YouTube" / "Reintentar".
 *
 * El bug del que se reportó: a veces el iframe se queda en pantalla negra
 * sin cargar el video (red lenta, YouTube rate-limit, fluctuación), y
 * el usuario solo veía un cuadro negro sin saber qué hacer. Ahora si
 * después de 8 segundos no disparó onload, mostramos un fallback con
 * 2 botones: reintentar (recarga el iframe) o abrir en YouTube nativo.
 */
function VideoIframeWithFallback({
  embedSrc,
  externalUrl,
  label,
}: {
  embedSrc: string;
  externalUrl: string;
  label: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (loaded) return;
    const t = setTimeout(() => setTimedOut(true), 8_000);
    return () => clearTimeout(t);
  }, [loaded, version]);

  const retry = () => {
    setLoaded(false);
    setTimedOut(false);
    setVersion(v => v + 1);
  };

  if (timedOut && !loaded) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4 text-white text-sm">
        <Video className="w-8 h-8 opacity-50" />
        <p className="text-center opacity-80">
          El video tardó en cargar. Puedes reintentar o abrirlo directo en {label}.
        </p>
        <div className="flex gap-2 mt-1">
          <button
            onClick={retry}
            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold transition-colors min-h-[40px]"
          >
            Reintentar
          </button>
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg bg-accent text-accent-foreground hover:opacity-90 text-xs font-bold transition-opacity min-h-[40px] flex items-center"
          >
            Abrir en {label}
          </a>
        </div>
      </div>
    );
  }

  return (
    <iframe
      key={version}
      src={embedSrc}
      className="w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      onLoad={() => setLoaded(true)}
    />
  );
}
